import {
  APIComponentAttributes,
  APIWorkspaceAttributes,
  ArdoqId,
  ProvenanceSource,
} from '@ardoq/api-types';
import { GetContextMenuOptionsArguments } from '@ardoq/context-menu';
import { getComponentMenu } from './componentMenu';
import { getReferenceMenu } from './referenceMenu';
import { getWorkspaceMenu, type WorkspaceMenuState } from './workspaceMenu';
import { getMixedEntitiesMenu } from './mixedEntitiesMenu';
import { type ReferenceComponents } from './referenceComponentsInterface';
import { PermissionContext } from '@ardoq/access-control';
import { getNonComponentGroupMenu } from './groupContextMenu';
import { componentInterface } from '@ardoq/component-interface';
import { toIds } from '@ardoq/core';
import { uniq } from 'lodash';
import { ContextMenuTestIds } from './consts';
import { type I18nLocale } from '@ardoq/i18n';
import { type GetScenarioContextMenuOptions } from 'scope/scenarioMenuOptions';
import { getFeatureContext } from 'streams/featureContext$';
import { lockOperations } from 'lockedComponents/lockOperations';
import { type WorkspaceAndModelLookup } from '@ardoq/data-model';
import { type ReferenceCreationMenuState } from 'streams/referenceCreationMenuState$';

type WorkspaceTargets =
  | {
      workspaces: APIWorkspaceAttributes[];
      workspaceMenuState: WorkspaceMenuState;
    }
  | { workspaces?: undefined; workspaceMenuState?: undefined };

export type ContextEntityTargets = {
  components?: APIComponentAttributes[];
  referenceComponents?: ReferenceComponents;
  eventTargetModelId?: ArdoqId;
  isEventTargetKnownEntity?: boolean;
  getNextTreeSelectionCandidate?: () => ArdoqId | null;
} & WorkspaceTargets;

export type ContextTargets = ContextEntityTargets & {
  workspaceAndModel: WorkspaceAndModelLookup;
  componentsById: Record<ArdoqId, APIComponentAttributes>;
  referenceCreation: ReferenceCreationMenuState;
};

const getCommonMenuData = (
  ctxMenuArguments: GetContextMenuOptionsArguments,
  targets: ContextTargets,
  i18nLocale: I18nLocale,
  permissionContext: PermissionContext,
  getScenarioContextMenuOptions: GetScenarioContextMenuOptions,
  provenanceSource?: ProvenanceSource
) => {
  const {
    components = [],
    referenceComponents = {
      references: [],
      sourceComponentsById: {},
      targetComponentsById: {},
      fullPathNamesById: {},
    },
    workspaces = [],
    eventTargetModelId,
    isEventTargetKnownEntity = false,
    getNextTreeSelectionCandidate = () => null,
    workspaceAndModel,
    componentsById,
    referenceCreation,
  } = targets;

  const { references } = referenceComponents;

  const componentIds = toIds(components);
  const referenceIds = toIds(references);
  const workspaceIds = toIds(workspaces);

  const hasReferences = referenceIds.length > 0;
  const hasComponents = componentIds.length > 0;
  const hasWorkspaces = workspaceIds.length > 0;

  if (
    ![hasReferences, hasComponents, hasWorkspaces, eventTargetModelId].some(
      Boolean
    )
  ) {
    return { options: null };
  }

  // a group which is not a component or a workspace (currently relevant for component type group).
  // if eventTargetModelId === id of the type group, filter options for component type will be shown
  if (
    [!hasReferences, !hasComponents, !hasWorkspaces].every(Boolean) &&
    eventTargetModelId
  ) {
    return {
      testId: ContextMenuTestIds.NON_COMPONENT_GROUP,
      options: getNonComponentGroupMenu({
        eventTargetModelId,
        isEventTargetKnownEntity,
        workspaceAndModel,
        clickedElement: ctxMenuArguments.target,
        isViewpointMode: ctxMenuArguments.isViewpointMode,
        i18nLocale,
      }),
    };
  }

  // Only components
  if (hasComponents && !hasReferences && !hasWorkspaces) {
    return {
      testId: ContextMenuTestIds.COMPONENT,
      options: getComponentMenu({
        ...ctxMenuArguments,
        components,
        eventTargetModelId,
        getNextTreeSelectionCandidate,
        permissionContext,
        getScenarioContextMenuOptions,
        workspaceAndModel,
        componentsById,
        referenceCreation,
        i18nLocale,
        provenanceSource,
      }),
      componentIds,
    };
  }

  // Only references
  if (hasReferences && !hasComponents && !hasWorkspaces) {
    const referenceWorkspaceIds = uniq(references.map(r => r.rootWorkspace));
    const hasLockedReferences = lockOperations.isAnyLocked(
      references.map(r => r.lock),
      getFeatureContext()
    );
    return {
      testId: ContextMenuTestIds.REFERENCE,
      options: getReferenceMenu({
        ...ctxMenuArguments,
        referenceComponents,
        workspaceIds: referenceWorkspaceIds,
        hasLockedReferences,
        permissionContext,
        workspaceAndModel,
        referenceCreation,
        i18nLocale,
      }),
      referenceIds,
    };
  }

  // Only workspaces
  if (targets.workspaces?.length && !hasComponents && !hasReferences) {
    return {
      testId: ContextMenuTestIds.WORKSPACE,
      options: getWorkspaceMenu({
        ...ctxMenuArguments,
        ...targets.workspaceMenuState,
        workspaces,
        permissionContext,
        i18nLocale,
      }),
      workspaceIds,
    };
  }

  // Mixed entities
  if (hasComponents && hasReferences) {
    const isScenarioContext = componentIds.some(
      componentInterface.isScenarioContextComponent
    );

    if (isScenarioContext) {
      return {
        options: null,
      };
    }

    return {
      testId: ContextMenuTestIds.COMPONENT_AND_REFERENCE,
      options: getMixedEntitiesMenu({
        ...ctxMenuArguments,
        components,
        referenceComponents,
        permissionContext,
        workspaceAndModel,
        referenceCreation,
        i18nLocale,
      }),
      componentIds,
      referenceIds,
    };
  }

  return { options: null };
};

export { getCommonMenuData };
